import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';

import * as invoicesApi from './invoices.api';

import type {
  ReceiveSupplierRefundRequest,
  SupplierRefundListQuery,
  SupplierRefundRow,
} from '@utazzo/shared';

import { extractServerError } from '@/lib/api-error';

/** React Query hooks for supplier refunds (ADMIN + FINANCE). */

const extractMessage = (err: unknown): string | null => extractServerError(err).message;

export const SUPPLIER_REFUNDS_QK = {
  all: () => ['invoices', 'supplier-refunds'] as const,
  list: (query: SupplierRefundListQuery) =>
    ['invoices', 'supplier-refunds', 'list', query] as const,
};

export const useSupplierRefunds = (query: SupplierRefundListQuery = {}) =>
  useQuery({
    queryKey: SUPPLIER_REFUNDS_QK.list(query),
    queryFn: () => invoicesApi.listSupplierRefunds(query),
    staleTime: 15_000,
  });

/** Mark a pending supplier refund as received. */
export const useReceiveSupplierRefund = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      payload,
    }: {
      id: string;
      payload: ReceiveSupplierRefundRequest;
    }): Promise<SupplierRefundRow> => invoicesApi.receiveSupplierRefund(id, payload),
    onSuccess: (row) => {
      void qc.invalidateQueries({ queryKey: SUPPLIER_REFUNDS_QK.all() });
      void qc.invalidateQueries({ queryKey: ['suppliers', 'ledger'] });
      void qc.invalidateQueries({ queryKey: ['invoices', 'detail', row.invoiceId] });
      void qc.invalidateQueries({ queryKey: ['invoices', 'edit-data', row.invoiceId] });
      toast.success('Refund marked as received');
    },
    onError: (err: unknown) => toast.error(extractMessage(err) ?? 'Failed to record refund'),
  });
};
